import { useState } from "react";
import { useMutation } from "@apollo/client/react";
import { useIntl } from "react-intl";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import {
  type CommunicationLanguage,
  EmailTemplatesDocument,
  UpdateSystemSettingsDocument,
} from "@/graphql/generated/operations";
import { COMMUNICATION_LANGUAGE_OPTIONS } from "@/lib/communication-language";
import { userErrorMessage } from "@/lib/user-error";
import { SettingsCard } from "./SettingsCard";

// The instance mail language (@external-adr GENESIS-0097-a-mail-is-written-in-the-language-resolved-for-its-recipient):
// every mail is written in it except one to an applicant whose application form recorded a language. One choice that
// saves at once, like a switch; it changes no language already fixed for a mail. Null means genesis holds an
// unsupported stored value: nothing is selected, mails needing it are not sent, and a choice repairs it.
export function InstanceMailLanguageCard({ value }: { value: CommunicationLanguage | null }) {
  const intl = useIntl();
  const [update, { loading }] = useMutation(UpdateSystemSettingsDocument, {
    refetchQueries: [{ query: EmailTemplatesDocument }],
    awaitRefetchQueries: true,
  });
  const [failure, setFailure] = useState<string | null>(null);
  const label = intl.formatMessage({
    id: "adminSettings.emailTemplates.instanceLanguage",
    defaultMessage: "Default email language",
  });

  async function choose(next: CommunicationLanguage | "UNKNOWN") {
    if (next === "UNKNOWN") return;
    if (next === value) return;
    setFailure(null);
    try {
      await update({ variables: { input: { data: { communicationLanguage: next } } } });
    } catch (cause) {
      setFailure(userErrorMessage(intl, cause));
    }
  }

  return (
    <SettingsCard
      title={label}
      sub={intl.formatMessage({
        id: "adminSettings.emailTemplates.instanceLanguageHint",
        defaultMessage:
          "Every email is written in this language, except one to an applicant whose application form recorded a language. The invitation is not affected.",
      })}
    >
      <SegmentedControl
        size="sm"
        aria-label={label}
        aria-busy={loading}
        value={value ?? "UNKNOWN"}
        onChange={(next) => void choose(next)}
        options={COMMUNICATION_LANGUAGE_OPTIONS.map((option) => ({ ...option, disabled: loading }))}
      />
      {value === null ? (
        <p role="status" className="mt-2 smycles-field-meta text-ink">
          {intl.formatMessage({
            id: "adminSettings.emailTemplates.instanceLanguageInvalid",
            defaultMessage:
              "The stored default language is invalid. Emails that need it are not sent until you choose a language.",
          })}
        </p>
      ) : null}
      {failure ? (
        <p role="alert" className="mt-2 smycles-field-meta text-ink">
          {failure}
        </p>
      ) : null}
    </SettingsCard>
  );
}
